/**
 * 展示管理模块 - 完整的CRUD功能
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, message, Form, Upload, InputNumber, Input, Space, Select, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { showcaseService } from '../../../../../services/showcaseService';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import { DictSelect } from '../../../../../components/common/DictSelect';
import { DICT_TYPES } from '../../../../../constants/dictTypes';
import { useDict } from '../../../../../hooks/useUnifiedDict';
import QueryArea, { QueryField } from '../../components/QueryArea';

const { TextArea } = Input;

// 状态开关组件 - 避免在style中直接调用form.getFieldValue
const StatusSwitch: React.FC<{ form: any; value?: any; onChange?: (checked: boolean) => void }> = ({ form, value, onChange }) => {
  const status = Form.useWatch('status', form);
  const checked = value !== undefined ? (value === 1) : (status === 1);
  
  const handleChange = (checked: boolean) => {
    if (onChange) {
      onChange(checked);
    } else {
      // 如果没有onChange prop，直接更新表单
      form.setFieldsValue({ status: checked ? 1 : 0 });
    }
  };
  
  return (
    <Switch 
      checked={checked}
      checkedChildren="正常" 
      unCheckedChildren="停用"
      className="status-switch"
      style={{ 
        backgroundColor: checked ? '#52c41a' : '#ff4d4f'
      }}
      onChange={handleChange}
    />
  );
};

// 状态开关样式和Upload图标提示
const statusSwitchStyle = `
  .status-switch.ant-switch-checked {
    background-color: #52c41a !important;
  }
  .status-switch:not(.ant-switch-checked) {
    background-color: #ff4d4f !important;
  }
  
  /* Upload图标提示文字 */
  .ant-upload-list-item-actions .anticon-eye {
    cursor: pointer;
  }
  .ant-upload-list-item-actions .anticon-delete {
    cursor: pointer;
  }
  
  /* 自定义图标提示 */
  .upload-preview-icon::before {
    content: "查看";
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  .upload-preview-icon:hover::before {
    opacity: 1;
  }
  
  .upload-delete-icon::before {
    content: "删除";
    position: absolute;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }
  
  .upload-delete-icon:hover::before {
    opacity: 1;
  }
  
  /* 自定义Upload组件样式 */
  .custom-upload-item {
    position: relative;
    display: inline-block;
  }
  
  .upload-actions {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 4px;
    opacity: 1;
    z-index: 10;
  }
  
  .custom-upload-item:hover .upload-actions {
    opacity: 1;
  }
  
  .upload-action-btn {
    color: #1890ff;
    cursor: pointer;
    padding: 4px;
    border-radius: 2px;
    transition: color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.95);
    border: 1px solid #d9d9d9;
  }
  
  .upload-action-btn:hover {
    color: #40a9ff;
  }
  
  /* 完全移除 Ant Design Upload 的悬停效果和文件名 */
  .ant-upload-list-item {
    border: 1px solid #d9d9d9 !important;
    transition: none !important;
  }
  
  .ant-upload-list-item:hover {
    background-color: transparent !important;
    background: transparent !important;
    border-color: #d9d9d9 !important;
  }
  
  .ant-upload-list-item:hover::before {
    display: none !important;
    opacity: 0 !important;
  }
  
  .ant-upload-list-item:hover .ant-upload-list-item-info {
    background-color: transparent !important;
    background: transparent !important;
  }
  
  .ant-upload-list-item:hover .ant-upload-list-item-info::before {
    display: none !important;
    opacity: 0 !important;
    background: none !important;
  }
  
  .ant-upload-list-item:hover .ant-upload-list-item-thumbnail {
    background-color: transparent !important;
    background: transparent !important;
  }
  
  /* 隐藏 Upload 组件的文件名显示 */
  .ant-upload-list-item-name,
  .ant-upload-list-item-name a,
  .ant-upload-list-item-name a span,
  .custom-upload-item .ant-upload-list-item-name,
  .custom-upload-item .ant-upload-list-item-name a {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    font-size: 0 !important;
    overflow: hidden !important;
  }
`;

interface Showcase {
  id: number;
  title: string;
  description: string;
  category: string;
  company: string;
  industry: string;
  technology: string; // 后端返回的是字符串，不是数组
  images: string;
  videoUrl?: string;
  status: number;
  viewCount: number;
  createTime: string;
}

const ShowcaseManagement: React.FC = () => {
  const [dataSource, setDataSource] = useState<Showcase[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    category: '',
    industry: '',
    company: '',
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Showcase | null>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [logoFileList, setLogoFileList] = useState<any[]>([]);

  // 使用数据字典
  // 只从缓存读取字典数据，不触发API调用
  const { getLabelByValue: getCategoryLabel } = useDict(DICT_TYPES.SHOWCASE_CATEGORY);
  const { getLabelByValue: getIndustryLabel } = useDict(DICT_TYPES.SHOWCASE_INDUSTRY);
  const { getLabelByValue: getTechnologyLabel } = useDict(DICT_TYPES.SHOWCASE_TECHNOLOGY);
  const { getLabelByValue: getStatusLabel } = useDict(DICT_TYPES.SHOWCASE_STATUS);

  // 查询字段配置
  const queryFields: QueryField[] = [
    {
      key: 'keyword',
      label: '项目名称',
      type: 'input',
      placeholder: '搜索项目名称...',
      width: 200,
    },
    {
      key: 'category',
      label: '分类',
      type: 'dict',
      dictType: DICT_TYPES.SHOWCASE_CATEGORY,
      placeholder: '选择分类',
      width: 150,
    },
    {
      key: 'company',
      label: '企业名称',
      type: 'input',
      placeholder: '搜索企业名称...',
      width: 150,
    },
    {
      key: 'industry',
      label: '所属行业',
      type: 'dict',
      dictType: DICT_TYPES.SHOWCASE_INDUSTRY,
      placeholder: '选择行业',
      width: 150,
    },
  ];

  // 表格列定义
  const columns: ColumnsType<Showcase> = [
    {
      title: '项目名称',
      dataIndex: 'title',
      key: 'title',
      width: 140,
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 80,
      render: (value) => getCategoryLabel(value) || value,
    },
    {
      title: '企业名称',
      dataIndex: 'company',
      key: 'company',
      width: 110,
      ellipsis: true,
    },
    {
      title: '所属行业',
      dataIndex: 'industry',
      key: 'industry',
      width: 80,
      render: (value) => getIndustryLabel(value) || value,
    },
    {
      title: '核心技术',
      dataIndex: 'technology',
      key: 'technology',
      width: 140,
      ellipsis: true,
      render: (value: string) => {
        if (!value) return '-';
        // 如果technology是字符串，尝试解析为数组
        try {
          const techArray = typeof value === 'string' ? value.split(',') : value;
          return techArray.map(tech => getTechnologyLabel(tech.trim()) || tech.trim()).join(', ');
        } catch {
          return value;
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const loadData = useCallback(async () => {
    if (loading) return; // 防止重复调用
    
    setLoading(true);
    try {
      const params = {
        current,
        size: pageSize,
        ...searchParams,
      };
      
      const response = await showcaseService.getList(params);
      if (response.code === 200) {
        setDataSource(response.data.records || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.message || '加载数据失败');
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchParams, loading]);

  // 使用useRef来跟踪是否已经初始化，避免重复调用
  const isInitialized = useRef(false);
  const lastSearchParams = useRef(searchParams);
  
  // 移除字典刷新逻辑：字典数据已经在登录时全部加载到本地
  // 这里只需要从缓存读取，不需要调用API刷新
  
  useEffect(() => {
    // 检查searchParams是否真正发生了变化
    const paramsChanged = JSON.stringify(searchParams) !== JSON.stringify(lastSearchParams.current);
    
    // 只在组件首次挂载时调用，或者当关键参数真正变化时调用
    if (!isInitialized.current || paramsChanged) {
      isInitialized.current = true;
      lastSearchParams.current = searchParams;
      loadData();
    }
  }, [current, pageSize, searchParams]); // 保持searchParams依赖，但添加变化检测

  // 模态框关闭时清空文件列表
  useEffect(() => {
    if (!isModalVisible) {
      setLogoFileList([]);
    }
  }, [isModalVisible]);

  // 搜索
  const handleSearch = () => {
    setCurrent(1);
    loadData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ keyword: '', category: '', industry: '', company: '' });
    setCurrent(1);
    setTimeout(loadData, 0);
  };

  // 处理查询参数变化
  const handleSearchParamsChange = useCallback((values: Record<string, any>) => {
    setSearchParams({
      keyword: values.keyword || '',
      category: values.category || '',
      industry: values.industry || '',
      company: values.company || '',
    });
  }, []);

  // 新增
  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setLogoFileList([]);
    setIsModalVisible(true);
  };

  // 查看
  const handleView = (record: Showcase) => {
    Modal.info({
      title: record.title,
      content: (
        <div>
          <p><strong>企业：</strong>{record.company}</p>
          <p><strong>行业：</strong>{record.industry}</p>
          <p><strong>核心技术：</strong>{record.technology || '-'}</p>
          <p><strong>描述：</strong>{record.description}</p>
          {record.videoUrl && <p><strong>视频：</strong><a href={record.videoUrl} target="_blank" rel="noopener noreferrer">查看视频</a></p>}
        </div>
      ),
      width: 600,
    });
  };

  // 编辑
  const handleEdit = (record: Showcase) => {
    // 先清空之前的文件列表
    setLogoFileList([]);
    
    // 处理图片数据
    let fileList: any[] = [];
    if (record.images && typeof record.images === 'string' && record.images.trim()) {
      try {
        // 分割图片字符串，处理可能存在的换行符等
        const imageStrings = record.images.split(',').map(s => s.trim()).filter(s => s);
        
        fileList = imageStrings.map((imageStr, index) => {
          // 确保Base64字符串完整
          let fullBase64 = imageStr.trim();
          
          // 如果Base64字符串不完整（没有data:image前缀），尝试修复
          if (!fullBase64.startsWith('data:image/')) {
            // 如果字符串以base64,开头，添加data:image/jpeg前缀
            if (fullBase64.startsWith('base64,')) {
              fullBase64 = 'data:image/jpeg;' + fullBase64;
            } else if (fullBase64.includes('base64,')) {
              const parts = fullBase64.split('base64,');
              if (parts.length > 1) {
                fullBase64 = 'data:image/jpeg;base64,' + parts[1];
              }
            } else {
              // 假设是纯Base64数据，添加完整前缀
              fullBase64 = 'data:image/jpeg;base64,' + fullBase64;
            }
          }
          
          // 验证Base64字符串
          if (fullBase64.startsWith('data:image/') && fullBase64.includes('base64,')) {
            return {
              uid: `existing-${record.id}-${index}-${Date.now()}`,
              name: `image-${index + 1}.jpg`,
              status: 'done' as const,
              url: fullBase64,
              thumbUrl: fullBase64,
            };
          } else {
            return null;
          }
        }).filter((item): item is NonNullable<typeof item> => item !== null);
      } catch (error) {
        fileList = [];
      }
    }
    
    // 设置文件列表
    setLogoFileList(fileList);
    
    // 设置编辑记录
    setEditingRecord(record);
    
    // 设置表单值，确保status正确转换
    form.setFieldsValue({
      ...record,
      status: record.status === 1 ? 1 : 0,
      technology: record.technology ? record.technology.split(',').filter(t => t.trim()) : [],
      images: record.images || '', // 保持原始值
    });
    
    setIsModalVisible(true);
  };

  // 删除
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个展示项目吗？删除后无法恢复。',
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await showcaseService.delete(id);
          if (response.code === 200) {
            message.success('删除成功');
            loadData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的数据');
      return;
    }
    
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个展示项目吗？删除后无法恢复。`,
      okText: '确定',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await showcaseService.batchDelete(selectedRowKeys as number[]);
          if (response.code === 200) {
            message.success('批量删除成功');
            setSelectedRowKeys([]);
            loadData();
          } else {
            message.error(response.message || '批量删除失败');
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败，请稍后重试');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // 表单提交
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // 处理图片数据 - 确保是字符串格式
      let imagesValue = '';
      if (typeof values.images === 'string' && values.images.trim()) {
        imagesValue = values.images;
      } else {
        // 如果表单值不是字符串，从logoFileList中获取
        const imageUrls = logoFileList.map(item => item.url || item.thumbUrl).filter(url => url && url.startsWith('data:image/'));
        imagesValue = imageUrls.join(',');
      }
      
      // 处理表单数据
      const formData: any = {
        // 处理技术标签
        technology: Array.isArray(values.technology) && values.technology.length > 0 
          ? values.technology.join(',') 
          : null,
        // 处理图片（确保是字符串格式）
        images: imagesValue || null,
        // 确保状态有默认值（Switch组件的checked值需要转换）
        status: values.status ? 1 : 0,
        // 确保viewCount有默认值
        viewCount: values.viewCount || editingRecord?.viewCount || 0,
        // 其他字段
        title: values.title,
        description: values.description,
        category: values.category,
        company: values.company,
        industry: values.industry,
        videoUrl: values.videoUrl,
      };
      
      
      let response;
      if (editingRecord) {
        response = await showcaseService.update({ ...formData, id: editingRecord.id });
      } else {
        response = await showcaseService.create(formData);
      }
      
      if (response.code === 200) {
        message.success(editingRecord ? '编辑成功' : '新增成功');
        // 先加载新数据，然后再关闭模态框和重置表单，避免状态闪烁
        await loadData();
        setIsModalVisible(false);
        // 延迟重置，确保模态框关闭动画完成后再重置
        setTimeout(() => {
          form.resetFields();
          setLogoFileList([]);
          setEditingRecord(null);
        }, 300);
      } else {
        message.error(response.message || '提交失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      message.error('提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="showcase-management">
      {/* 添加状态开关样式 */}
      <style>{statusSwitchStyle}</style>
      
      {/* 统一查询区域 */}
      <QueryArea
        fields={queryFields}
        values={searchParams}
        onValuesChange={handleSearchParamsChange}
        onAdd={handleAdd}
        onBatchDelete={handleBatchDelete}
        batchDeleteDisabled={selectedRowKeys.length === 0}
        selectedCount={selectedRowKeys.length}
        addButtonText="新增"
        batchDeleteButtonText="批量删除"
      />

      {/* 数据表格 */}
      <div className="table-card">
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            current,
            pageSize,
            total,
            ...PAGINATION_CONFIG.default,
            onChange: (page, size) => {
              setCurrent(page);
              setPageSize(size);
            },
          }}
        />
      </div>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑展示项目' : '新增展示项目'}
        open={isModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          // 延迟重置，确保模态框关闭动画完成后再重置
          setTimeout(() => {
            form.resetFields();
            setLogoFileList([]);
            setEditingRecord(null);
          }, 300);
        }}
        width={800}
        confirmLoading={loading}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
          initialValues={{
            status: 1,
            technology: [],
            images: '',
          }}
          preserve={false}
        >
          <Form.Item
            name="title"
            label="项目名称"
            rules={[{ required: true, message: '请输入项目名称' }]}
          >
            <Input placeholder="请输入项目名称" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="category"
              label="分类"
              rules={[{ required: true, message: '请选择分类' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <DictSelect
                dictType={DICT_TYPES.SHOWCASE_CATEGORY}
                placeholder="请选择分类"
              />
            </Form.Item>

            <Form.Item
              name="industry"
              label="所属行业"
              rules={[{ required: true, message: '请选择所属行业' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <DictSelect
                dictType={DICT_TYPES.SHOWCASE_INDUSTRY}
                placeholder="请选择所属行业"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="description"
            label="项目描述"
            rules={[{ required: true, message: '请输入项目描述' }]}
          >
            <TextArea rows={4} placeholder="请输入项目描述" />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="company"
              label="企业名称"
              rules={[{ required: true, message: '请输入企业名称' }]}
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Input placeholder="请输入企业名称" />
            </Form.Item>

            <Form.Item
              name="status"
              label="状态"
              style={{ flex: 1 }}
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
              valuePropName="checked"
              getValueFromEvent={(checked) => {
                return checked ? 1 : 0;
              }}
              getValueProps={(value) => {
                return { checked: value === 1 };
              }}
              normalize={(value) => {
                return value === 1 || value === true ? 1 : 0;
              }}
            >
              <StatusSwitch form={form} />
            </Form.Item>
          </div>

          <Form.Item
            name="technology"
            label="核心技术"
            rules={[{ required: true, message: '请选择核心技术' }]}
          >
            <DictSelect
              dictType={DICT_TYPES.SHOWCASE_TECHNOLOGY}
              placeholder="请选择核心技术"
              mode="multiple"
            />
          </Form.Item>

          <Form.Item
            name="videoUrl"
            label="链接"
          >
            <Input placeholder="请输入链接地址" />
          </Form.Item>

          <Form.Item
            name="images"
            label="项目图片"
          >
            <Upload
              listType="picture-card"
              maxCount={5}
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
              fileList={logoFileList}
              onChange={(info) => {
                // 处理文件列表变化
                const newFileList = info.fileList
                  .map(file => {
                    // 确保每个文件都有正确的url和thumbUrl
                    const url = file.url || file.thumbUrl || (file.response?.url);
                    if (!url) {
                      return null;
                    }
                    return {
                      ...file,
                      url: url,
                      thumbUrl: url,
                    };
                  })
                  .filter((file): file is NonNullable<typeof file> => file !== null && !!file.url);
                
                setLogoFileList(newFileList);
                
                // 更新表单值
                const imageUrls = newFileList
                  .map(item => item.url || item.thumbUrl)
                  .filter((url): url is string => !!url && typeof url === 'string' && url.startsWith('data:image/'));
                
                form.setFieldsValue({ images: imageUrls.join(',') });
              }}
              onRemove={(file) => {
                const newList = logoFileList.filter(item => item.uid !== file.uid);
                setLogoFileList(newList);
                const imageUrls = newList
                  .map(item => item.url || item.thumbUrl)
                  .filter(url => url && url.startsWith('data:image/'));
                form.setFieldsValue({ images: imageUrls.join(',') || '' });
                return true; // 允许删除
              }}
              onPreview={(file) => {
                const imageUrl = file.url || file.thumbUrl;
                if (imageUrl) {
                  setPreviewImage(imageUrl);
                  setPreviewTitle('项目图片预览');
                  setPreviewVisible(true);
                }
              }}
              beforeUpload={(file) => {
                // 检查文件类型
                const isImage = file.type.startsWith('image/');
                if (!isImage) {
                  message.error('只能上传图片文件！');
                  return Upload.LIST_IGNORE;
                }
                
                // 检查文件大小 (2MB)
                const isLt2M = file.size / 1024 / 1024 < 2;
                if (!isLt2M) {
                  message.error('图片大小不能超过 2MB！');
                  return Upload.LIST_IGNORE;
                }
                
                // 压缩图片并转换为Base64
                const reader = new FileReader();
                const img = new Image();
                
                reader.onload = (e) => {
                  const result = e.target?.result as string;
                  img.src = result;
                };
                
                img.onload = () => {
                  // 计算压缩后的尺寸（最大800x600）
                  const maxWidth = 800;
                  const maxHeight = 600;
                  let { width, height } = img;
                  
                  if (width > height) {
                    if (width > maxWidth) {
                      height = (height * maxWidth) / width;
                      width = maxWidth;
                    }
                  } else {
                    if (height > maxHeight) {
                      width = (width * maxHeight) / height;
                      height = maxHeight;
                    }
                  }
                  
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = width;
                  canvas.height = height;
                  ctx!.drawImage(img, 0, 0, width, height);
                  
                  // 转换为Base64
                  const base64 = canvas.toDataURL('image/jpeg', 0.8);
                  
                  // 创建文件对象并添加到列表
                  const newFile = {
                    uid: `upload-${Date.now()}-${Math.random()}`,
                    name: file.name,
                    status: 'done' as const,
                    url: base64,
                    thumbUrl: base64,
                  };
                  
                  setLogoFileList(prev => {
                    const newList = [...prev, newFile];
                    const imageUrls = newList
                      .map(item => item.url || item.thumbUrl)
                      .filter(url => url && url.startsWith('data:image/'));
                    form.setFieldsValue({ images: imageUrls.join(',') });
                    return newList;
                  });
                };
                
                img.onerror = () => {
                  message.error('图片加载失败，请重试');
                };
                
                reader.readAsDataURL(file);
                
                // 阻止默认上传
                return false;
              }}
            >
              {logoFileList.length >= 5 ? null : (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>上传图片</div>
              </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 图片预览Modal */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={600}
        styles={{ body: { height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
      >
        <img alt="preview" style={{ maxWidth: '100%', maxHeight: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default ShowcaseManagement;